import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, LoadingController } from '@ionic/angular';

import { Tenant } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { MessageService } from 'src/services/message.service';
import { TenantsService } from 'src/services/tenants.service';

import { SharedTenantService } from '../shared-tenant.service';

interface TaxonomyCategory {
  category: string;
  articleCount: number;
}

interface TaxonomyCategoryArticle {
  id: string;
  title: string;
  projectId: string | null;
}

interface TaxonomyCategoryDetailed {
  categoryId: string | null;
  category: string;
  scope: 'global' | 'project';
  projectId: string | null;
  dimensionId: string;
  dimensionName: string;
  articleCount: number;
  articles: TaxonomyCategoryArticle[];
}

interface TaxonomyCategoriesResponse {
  categories: TaxonomyCategoryDetailed[];
  dimensions: Array<{ id: string; name: string; role: string | null }>;
  meta: {
    description: string;
    lastUpdated: string;
    dimensionId?: string;
    dimensionName?: string;
  };
}

interface TaxonomyDimensionStats {
  dimensionId: string;
  dimensionName: string;
  strategy: string;
  totalArticles: number;
  classifiedArticles: number;
  progressPercent: number;
  categories: TaxonomyCategory[];
  isComplete: boolean;
}

interface TaxonomyStats {
  tenantId: string;
  dimensions: TaxonomyDimensionStats[];
  summary: {
    totalDimensions: number;
    activeDimensions: number;
    completedDimensions: number;
  };
}

interface StrategySettings {
  strategy: string;
  confidenceThresholds: {
    llm: number;
    centroid: number;
  };
  availableStrategies: string[];
  strategyDescriptions?: {
    [key: string]: string;
  };
}

interface TaxonomyResponse {
  taxonomyStats: TaxonomyStats;
  strategySettings: StrategySettings;
  meta: {
    description: string;
    lastUpdated: string;
  };
}

@Component({
  selector: 'app-taxonomy-dashboard',
  templateUrl: './taxonomy-dashboard.component.html',
  styleUrls: ['./taxonomy-dashboard.component.scss'],
})
export class TaxonomyDashboardComponent implements OnInit {
  stats: TaxonomyResponse | null = null;
  categories: TaxonomyCategoriesResponse | null = null;
  currentDimension: TaxonomyDimensionStats | null = null;
  isLoading = false;
  isLoadingCategories = false;
  error: string | null = null;
  tenant: Partial<Tenant> | null = null;
  dimensionId: string | null = null;

  // Confidence threshold editing
  isEditingThresholds = false;
  editThresholds = {
    llm: 0,
    centroid: 0,
  };
  isSavingThresholds = false;

  // Onboarding options
  onboardingOptions = {
    skipExisting: true,
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private messageService: MessageService,
    private sharedTenantService: SharedTenantService,
    private tenantsService: TenantsService,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      const tenantId = params.get('tenantId');
      this.dimensionId = params.get('dimensionId');

      if (tenantId && this.dimensionId) {
        // Try to get tenant from shared service first
        const sharedTenant = this.sharedTenantService.getSelectedTenant();
        if (sharedTenant && sharedTenant.id === tenantId) {
          this.tenant = sharedTenant;
          this.loadTaxonomyData();
          this.loadCategories();
        } else {
          // Fetch tenant data directly from API
          this.loadTenantAndData(tenantId);
        }
      } else {
        this.error = 'Missing tenant ID or dimension ID in URL.';
      }
    });
  }

  loadTenantAndData(tenantId: string) {
    this.isLoading = true;
    this.error = null;

    // Fetch full tenant data including subdomain
    this.tenantsService
      .getTenantById(tenantId)
      .then((tenant) => {
        this.tenant = tenant;
        this.sharedTenantService.setSelectedTenant(tenant);
        this.loadTaxonomyData();
        this.loadCategories();
      })
      .catch((error) => {
        this.isLoading = false;
        this.error = 'Failed to load tenant: ' + (error.message || 'Unknown error');
      });
  }

  loadTaxonomyData() {
    if (!this.tenant?.id || !this.dimensionId) return;

    this.isLoading = true;
    this.error = null;
    this.stats = null;

    this.apiService
      .get<TaxonomyResponse>(`/knowledge-base/taxonomy/${this.tenant.id}/dimensions/${this.dimensionId}`)
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.stats = response;

          // Find the current dimension from the response
          this.currentDimension =
            response.taxonomyStats.dimensions.find((d) => d.dimensionId === this.dimensionId) || null;

          // Update tenant name if we only have a minimal tenant object
          if (this.tenant?.name === 'Loading...') {
            // Use tenant ID as fallback display name
            this.tenant = { ...this.tenant, name: `Tenant ${this.tenant.id}` };
            this.sharedTenantService.setSelectedTenant(this.tenant);
          }
        },
        error: (err) => {
          this.isLoading = false;
          this.error = 'Failed to load taxonomy data: ' + (err.message || 'Unknown error');
        },
      });
  }

  loadCategories() {
    if (!this.tenant?.id || !this.dimensionId) return;

    this.isLoadingCategories = true;

    this.apiService
      .get<TaxonomyCategoriesResponse>(
        `/knowledge-base/taxonomy/${this.tenant.id}/dimensions/${this.dimensionId}/categories`,
      )
      .subscribe({
        next: (response) => {
          this.categories = response;
          this.isLoadingCategories = false;
        },
        error: (err) => {
          this.isLoadingCategories = false;
          this.messageService.showError('Failed to load categories: ' + (err.message || 'Unknown error'));
        },
      });
  }

  async startOnboarding() {
    if (!this.tenant?.id || !this.dimensionId) return;

    const alert = await this.alertController.create({
      header: 'Start Taxonomy Onboarding',
      message: 'This will begin the process of automatically categorizing articles for this dimension.',
      inputs: [
        {
          name: 'skipExisting',
          type: 'checkbox',
          label: 'Skip articles that are already classified',
          value: 'skipExisting',
          checked: this.onboardingOptions.skipExisting,
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Start',
          handler: async (data) => {
            // Update the option based on checkbox
            this.onboardingOptions.skipExisting = data.includes('skipExisting');

            const loading = await this.loadingController.create({
              message: 'Starting onboarding...',
            });
            await loading.present();

            try {
              await this.apiService
                .post(`/knowledge-base/taxonomy/${this.tenant?.id}/dimensions/${this.dimensionId}/start-onboarding`, {
                  skipExisting: this.onboardingOptions.skipExisting,
                })
                .toPromise();
              this.messageService.show('Onboarding started successfully');
              this.loadTaxonomyData();
            } catch (error: any) {
              this.messageService.showError('Failed to start onboarding: ' + (error.message || 'Unknown error'));
            } finally {
              loading.dismiss();
            }
          },
        },
      ],
    });

    await alert.present();
  }

  async resetTaxonomy() {
    if (!this.tenant?.id || !this.dimensionId) return;

    const alert = await this.alertController.create({
      header: 'Reset Taxonomy',
      message:
        'This will remove all categories and classifications for this dimension. This action cannot be undone. Are you sure?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Reset',
          cssClass: 'danger',
          handler: async () => {
            const loading = await this.loadingController.create({
              message: 'Resetting taxonomy...',
            });
            await loading.present();

            try {
              await this.apiService
                .post(`/knowledge-base/taxonomy/${this.tenant?.id}/dimensions/${this.dimensionId}/reset`, {})
                .toPromise();
              this.messageService.show('Taxonomy reset successfully');
              this.loadTaxonomyData();
              this.loadCategories();
            } catch (error: any) {
              this.messageService.showError('Failed to reset taxonomy: ' + (error.message || 'Unknown error'));
            } finally {
              loading.dismiss();
            }
          },
        },
      ],
    });

    await alert.present();
  }

  viewDimensionalAnalysis() {
    if (!this.tenant?.id || !this.dimensionId) return;
    this.router.navigate(['/super-admin/taxonomy', this.tenant.id, this.dimensionId, 'system-confidence-analysis']);
  }

  onRefresh() {
    this.loadTaxonomyData();
    this.loadCategories();
  }

  goBack() {
    this.router.navigate(['/super-admin/taxonomy', this.tenant?.id]);
  }

  getProgressColor(percent: number): string {
    if (percent === 100) return 'success';
    if (percent >= 75) return 'primary';
    if (percent >= 25) return 'warning';
    return 'danger';
  }

  getCompletionText(isComplete: boolean): string {
    return isComplete ? 'Excellent' : 'In Progress';
  }

  getCompletionColor(isComplete: boolean): string {
    return isComplete ? 'success' : 'warning';
  }

  viewCategoryAnalysis(categoryName: string) {
    if (!this.tenant?.id || !this.dimensionId || !this.categories) return;

    // Find the detailed category with centroid ID
    const detailedCategory = this.categories.categories.find((c) => c.category === categoryName);
    if (!detailedCategory?.categoryId) {
      this.messageService.showError('Category ID not found');
      return;
    }

    this.router.navigate([
      '/super-admin/taxonomy',
      this.tenant.id,
      this.dimensionId,
      'category',
      detailedCategory.categoryId,
    ]);
  }

  getStrategyColor(strategy: string): string {
    switch (strategy.toLowerCase()) {
      case 'large':
        return 'success';
      case 'small-llm':
        return 'warning';
      case 'small-centroid':
        return 'secondary';
      case 'small-hybrid':
        return 'tertiary';
      default:
        return 'medium';
    }
  }

  trackByCategory(index: number, category: TaxonomyCategory): string {
    return category.category;
  }

  trackByCategoryDetailed(index: number, category: TaxonomyCategoryDetailed): string {
    return category.categoryId || category.category;
  }

  trackByArticle(index: number, article: TaxonomyCategoryArticle): string {
    return article.id;
  }

  getTaxonomyArticleUrl(articleId: string): string {
    // Link to taxonomy article analysis within the current dimension context
    if (!this.tenant?.id || !this.dimensionId) {
      return '#';
    }
    return `/super-admin/taxonomy/${this.tenant.id}/${this.dimensionId}/article/${articleId}`;
  }

  getTenantArticleUrl(articleId: string): string {
    // Link to tenant's subdomain knowledge base edit page
    if (!this.tenant?.subdomain) {
      return '#';
    }

    // Generate URL to tenant's subdomain using tenant.subdomain property
    const currentUrl = new URL(window.location.href);

    // Extract the base domain by splitting the hostname and taking the last two parts
    const hostnameParts = currentUrl.hostname.split('.');
    const baseDomain = hostnameParts.length > 2 ? hostnameParts.slice(-2).join('.') : currentUrl.hostname;

    // Include port if it exists
    const port = currentUrl.port ? `:${currentUrl.port}` : '';
    const tenantUrl = `${currentUrl.protocol}//${this.tenant.subdomain}.${baseDomain}${port}/dashboard/knowledge/articles/${articleId}/edit`;
    return tenantUrl;
  }

  onTaxonomyArticleClick(event: MouseEvent, articleId: string) {
    // Check for modifier keys (Ctrl, Cmd, Shift) or middle mouse button
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1) {
      // Let the browser handle it (open in new tab, etc.)
      return true;
    }

    // For regular clicks, prevent default and navigate using Angular router
    event.preventDefault();

    // Navigate to taxonomy article analysis using Angular router
    const url = this.getTaxonomyArticleUrl(articleId);
    if (url !== '#') {
      this.router.navigateByUrl(url);
    }

    return false;
  }

  startEditingThresholds() {
    if (!this.stats?.strategySettings?.confidenceThresholds) return;

    this.editThresholds = {
      llm: +this.stats.strategySettings.confidenceThresholds.llm,
      centroid: +this.stats.strategySettings.confidenceThresholds.centroid,
    };
    this.isEditingThresholds = true;
  }

  cancelEditingThresholds() {
    this.isEditingThresholds = false;
    this.editThresholds = { llm: 0, centroid: 0 };
  }

  async saveThresholds() {
    if (!this.tenant?.id || !this.dimensionId) return;

    // Validate threshold values (they're already numbers)
    if (
      isNaN(this.editThresholds.llm) ||
      this.editThresholds.llm < 0 ||
      this.editThresholds.llm > 1 ||
      isNaN(this.editThresholds.centroid) ||
      this.editThresholds.centroid < 0 ||
      this.editThresholds.centroid > 1
    ) {
      this.messageService.showError('All threshold values must be valid numbers between 0 and 1');
      return;
    }

    this.isSavingThresholds = true;

    const payload = {
      llmThreshold: this.editThresholds.llm,
      centroidThreshold: this.editThresholds.centroid,
    };

    try {
      await this.apiService
        .put(`/knowledge-base/taxonomy/${this.tenant.id}/taxonomy-dimensions/${this.dimensionId}`, payload)
        .toPromise();

      this.messageService.show('Confidence thresholds updated successfully');
      this.isEditingThresholds = false;
      this.loadTaxonomyData(); // Reload to get updated values
    } catch (error: any) {
      this.messageService.showError('Failed to update thresholds: ' + (error.message || 'Unknown error'));
    } finally {
      this.isSavingThresholds = false;
    }
  }
}
